"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REGISTRATION_REQUEST_EXAMPLES = exports.registerInsuranceRequestSchema = exports.registerReferralRequestSchema = exports.registerCorporateRequestSchema = exports.registerNewbornRequestSchema = exports.registerUnknownRequestSchema = exports.registerEmergencyRequestSchema = exports.registerPatientRequestSchema = void 0;
/**
 * Request contracts for the 6 named registration endpoints. Field
 * shapes mirror each Domain Service's own input interface exactly
 * (`backend/src/modules/patient/registration/*.service.ts`, frozen,
 * Stage 4C.2) — this package does not import backend source, so the
 * shapes are re-declared here as the wire contract, kept in lockstep
 * by convention. `RegisterInsurancePatient` has no contract yet — see
 * `docs/PATIENT-API-CONTRACT-GUIDE.md`'s named gap (this stage's brief
 * names 6 registration variants, not 7).
 */
const zod_1 = require("zod");
const contactSchema = zod_1.z.object({
    contactType: zod_1.z.enum(['PHONE', 'EMAIL']),
    value: zod_1.z.string().min(1).max(255),
    isPrimary: zod_1.z.boolean().optional(),
});
const addressSchema = zod_1.z.object({
    addressType: zod_1.z.enum(['CURRENT', 'PERMANENT', 'TEMPORARY']),
    addressLine1: zod_1.z.string().min(1).max(255),
    addressLine2: zod_1.z.string().max(255).optional(),
    areaId: zod_1.z.string().uuid().optional(),
    countryId: zod_1.z.string().uuid(),
    stateId: zod_1.z.string().uuid().optional(),
    cityId: zod_1.z.string().uuid().optional(),
    postalCode: zod_1.z.string().max(20).optional(),
});
// ADR-034 Decision 2 — optional side-table inputs, additive to every
// registration request schema below. Absent for existing callers
// sending the pre-ADR-034 shape (byte-for-byte unaffected); when
// present, patient-registration-helpers.ts's new
// createOptionalIdentityDocuments/createOptionalRecordProtectionFlags/
// createOptionalSideTableRows call the real, existing services/
// repositories that already own this data (AddIdentityDocumentService,
// ApplyRecordProtectionService, AddEmergencyContactService,
// AddGuardianService, PatientCommunicationPreferenceRepository,
// PatientSocialHistoryRepository) — never reimplemented here.
const identityDocumentInputSchema = zod_1.z.object({
    documentTypeId: zod_1.z.string().uuid(),
    documentNumber: zod_1.z.string().min(1),
    issuingAuthority: zod_1.z.string().max(150).optional(),
    expiryDate: zod_1.z.coerce.date().optional(),
});
const recordProtectionFlagInputSchema = zod_1.z.object({
    protectionCategory: zod_1.z.enum(['LEGAL', 'PRIVACY', 'INVESTIGATION', 'FREEZE', 'RETENTION']),
    protectionTypeId: zod_1.z.string().uuid(),
    durationType: zod_1.z.enum(['TEMPORARY', 'PERMANENT']),
    expiresAt: zod_1.z.coerce.date().optional(),
    reasonCode: zod_1.z.string().max(100).optional(),
    reasonDescription: zod_1.z.string().optional(),
    /**
     * The document number the flag rests on — an MLC number, an FIR number, a
     * court-order reference. `record_protections.supporting_reference_value`
     * has always existed for exactly this and had no way in through
     * registration, so an MLC captured at the desk could record THAT it is one
     * and not WHICH case it is.
     */
    supportingReferenceValue: zod_1.z.string().max(255).optional(),
});
const emergencyContactInputSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(255),
    relationshipTypeId: zod_1.z.string().uuid(),
    phone: zod_1.z.string().min(1).max(20),
    alternatePhone: zod_1.z.string().max(20).optional(),
});
const guardianInputSchema = zod_1.z.object({
    guardianPatientId: zod_1.z.string().uuid().optional(),
    unregisteredGuardianName: zod_1.z.string().max(255).optional(),
    unregisteredGuardianContact: zod_1.z.string().max(100).optional(),
    guardianType: zod_1.z.enum(['PARENT', 'COURT_APPOINTED', 'POWER_OF_ATTORNEY', 'OTHER']),
});
const socialHistoryInputSchema = zod_1.z.object({
    occupation: zod_1.z.string().max(150).optional(),
    smokingStatusId: zod_1.z.string().uuid().optional(),
    alcoholUseStatusId: zod_1.z.string().uuid().optional(),
});
const communicationPreferenceInputSchema = zod_1.z.object({
    preferredChannelId: zod_1.z.string().uuid(),
    preferredLanguageId: zod_1.z.string().uuid().optional(),
    doNotContact: zod_1.z.boolean().optional(),
});
const optionalRegistrationExtrasSchema = zod_1.z.object({
    identityDocuments: zod_1.z.array(identityDocumentInputSchema).optional(),
    recordProtectionFlags: zod_1.z.array(recordProtectionFlagInputSchema).optional(),
    emergencyContacts: zod_1.z.array(emergencyContactInputSchema).optional(),
    guardians: zod_1.z.array(guardianInputSchema).optional(),
    socialHistory: socialHistoryInputSchema.optional(),
    communicationPreference: communicationPreferenceInputSchema.optional(),
    // Which RegistrationPatientCategory this registration represents, for
    // the Compliance Check's role/department/category override resolution
    // (ADR-034 Decision 8) — optional, defaults are mode-specific (see
    // registration-config-resolver.service.ts).
    patientCategory: zod_1.z
        .enum(['SELF_PAY', 'CORPORATE', 'INSURANCE', 'GOVERNMENT_SCHEME', 'INTERNATIONAL', 'EMPLOYEE', 'DEPENDENT'])
        .optional(),
    // ADR-034 — QUICK vs DETAILED is an independent axis from which of the
    // 7 registration endpoints is called (a Reception user might submit a
    // QUICK `POST /patients`, an Admission Desk user a DETAILED one).
    // Defaults to DETAILED when omitted so existing callers who have never
    // heard of this field keep seeing the full, pre-ADR-034 field set
    // resolved, not a suddenly-trimmed one.
    registrationMode: zod_1.z.enum(['QUICK', 'DETAILED']).optional(),
    // Which registration counter is issuing this patient's UHID. REQUIRED on
    // every registration: the hospital registers under exactly three counters
    // (OPD / CR / Children) and each runs its own UHID series, so there is no
    // such thing as a registration without one. Enforced again in
    // `allocateUhid()` so an API client cannot route around this schema.
    uhidCounterType: zod_1.z.enum(['OPD', 'CR', 'CHILDREN'], {
        required_error: 'Select the registration counter (OPD, CR or Children)',
    }),
    // Registered as a staff member's dependent. `employeeId` is only meaningful
    // with the flag set, and the database enforces that pairing too, so a client
    // cannot attach an employee to a patient who is not marked a dependent.
    isEmployeeDependent: zod_1.z.boolean().optional(),
    employeeId: zod_1.z.string().uuid().optional(),
});
const baseDemographicsSchema = zod_1.z.object({
    givenName: zod_1.z.string().min(1).max(150),
    middleName: zod_1.z.string().max(150).optional(),
    familyName: zod_1.z.string().max(150).optional(),
    dateOfBirth: zod_1.z.coerce.date().optional(),
    dateOfBirthPrecision: zod_1.z.enum(['EXACT', 'YEAR_ONLY', 'ESTIMATED']).optional(),
    genderId: zod_1.z.string().uuid().optional(),
    maritalStatusId: zod_1.z.string().uuid().optional(),
    bloodGroupId: zod_1.z.string().uuid().optional(),
    religionId: zod_1.z.string().uuid().optional(),
    nationalityId: zod_1.z.string().uuid().optional(),
    salutationId: zod_1.z.string().uuid().optional(),
    qualificationId: zod_1.z.string().uuid().optional(),
    isNewborn: zod_1.z.boolean().optional(),
    // Front-desk identity qualifier (S/O, D/O, W/O, H/O + the named person) —
    // a plain scalar pair on Patient, not the existing PatientRelationship
    // graph model (see that model's own doc comment for why).
    relationTypeId: zod_1.z.string().uuid().optional(),
    relationName: zod_1.z.string().max(255).optional(),
    primaryBranchId: zod_1.z.string().uuid().optional(),
});
exports.registerPatientRequestSchema = baseDemographicsSchema.extend({
    contacts: zod_1.z.array(contactSchema).min(1, 'At least one contact method is required'),
    addresses: zod_1.z.array(addressSchema).optional(),
    ...optionalRegistrationExtrasSchema.shape,
});
exports.registerEmergencyRequestSchema = zod_1.z.object({
    givenName: zod_1.z.string().min(1).max(150),
    familyName: zod_1.z.string().max(150).optional(),
    primaryBranchId: zod_1.z.string().uuid().optional(),
    completionWindowHours: zod_1.z.number().int().positive().optional(),
    ...optionalRegistrationExtrasSchema.shape,
});
exports.registerUnknownRequestSchema = zod_1.z.object({
    placeholderGivenName: zod_1.z.string().min(1).max(150),
    foundLocation: zod_1.z.string().min(1),
    foundCircumstances: zod_1.z.string().min(1),
    primaryBranchId: zod_1.z.string().uuid().optional(),
    ...optionalRegistrationExtrasSchema.shape,
});
// A newborn is registered at the Children counter by definition, so the
// counter is not the caller's to choose — the field is omitted rather than
// made optional, so passing one is a contract error rather than a value that
// gets quietly ignored.
exports.registerNewbornRequestSchema = zod_1.z
    .object({
    motherPatientId: zod_1.z.string().uuid(),
    birthAdmissionId: zod_1.z.string().uuid(),
    birthOrder: zod_1.z.number().int().min(1),
    dateOfBirth: zod_1.z.coerce.date(),
    genderId: zod_1.z.string().uuid().optional(),
    primaryBranchId: zod_1.z.string().uuid().optional(),
    ...optionalRegistrationExtrasSchema.shape,
})
    .omit({ uhidCounterType: true });
exports.registerCorporateRequestSchema = exports.registerPatientRequestSchema.extend({
    payerId: zod_1.z.string().uuid(),
    policyNumber: zod_1.z.string().min(1),
    groupNumber: zod_1.z.string().min(1),
    relationshipToSubscriberId: zod_1.z.string().uuid(),
});
exports.registerReferralRequestSchema = exports.registerPatientRequestSchema.extend({
    referringFacilityName: zod_1.z.string().min(1),
    referringProviderName: zod_1.z.string().optional(),
    reason: zod_1.z.string().min(1),
});
// ADR-034 — RegisterInsurancePatientService already had real domain
// logic (backend/src/modules/patient/registration/register-insurance-patient.service.ts)
// but no contract/route ever existed for it (a pre-existing, disclosed
// dead-code gap this stage closes so "every mode is config-driven"
// doesn't need a documented exception for a 7th mode).
exports.registerInsuranceRequestSchema = exports.registerPatientRequestSchema.extend({
    payerId: zod_1.z.string().uuid(),
    policyNumber: zod_1.z.string().min(1),
    relationshipToSubscriberId: zod_1.z.string().uuid(),
    cashlessEligible: zod_1.z.boolean().optional(),
    preAuthRequired: zod_1.z.boolean().optional(),
});
exports.REGISTRATION_REQUEST_EXAMPLES = {
    registerPatient: {
        givenName: 'Asha',
        familyName: 'Rao',
        dateOfBirth: new Date('1990-04-12'),
        contacts: [{ contactType: 'PHONE', value: '+919999999999', isPrimary: true }],
        // REQUIRED, and it was missing here while the two examples below carried
        // it — so the ONE example a reader is most likely to copy was the one that
        // could not be posted. `satisfies Partial<…>` is what let it compile: a
        // partial type cannot notice an absent mandatory field, which is exactly
        // how a published example drifts out of agreement with its own schema.
        uhidCounterType: 'OPD',
    },
    registerEmergency: {
        givenName: 'Unknown Male',
        completionWindowHours: 24,
        uhidCounterType: 'OPD',
    },
    registerUnknown: {
        placeholderGivenName: 'Unknown Female',
        foundLocation: 'NH-4 highway, near Toll Plaza',
        foundCircumstances: 'Found unconscious by passerby, brought in by police',
        uhidCounterType: 'OPD',
    },
    registerNewborn: {
        motherPatientId: '22222222-2222-2222-2222-222222222222',
        birthAdmissionId: '33333333-3333-3333-3333-333333333333',
        birthOrder: 1,
        dateOfBirth: new Date('2026-07-10'),
    },
    registerCorporate: {
        givenName: 'Rahul',
        familyName: 'Mehta',
        contacts: [{ contactType: 'PHONE', value: '+919888888888', isPrimary: true }],
        payerId: '88888888-8888-8888-8888-888888888888',
        policyNumber: 'CORP-POLICY-456789',
        groupNumber: 'ACME-CORP-2026',
        relationshipToSubscriberId: '55555555-5555-5555-5555-555555555555',
        uhidCounterType: 'CR',
    },
    registerReferral: {
        givenName: 'Priya',
        familyName: 'Nair',
        contacts: [{ contactType: 'PHONE', value: '+919777777777', isPrimary: true }],
        referringFacilityName: 'City Government Hospital',
        reason: 'Referred for specialist cardiology consultation',
        uhidCounterType: 'OPD',
    },
};
//# sourceMappingURL=registration.request.js.map